<template>
  <div class="relative isolate flex size-full flex-col overflow-y-auto">
    <div
      class="sticky top-0 z-40 mb-px flex w-full items-center gap-2 bg-white p-3 px-5 leading-none text-black md:py-4"
    >
      <h2 class="text-lg font-bold">
        {{ t('product.options.text_option') }}&nbsp;{{
          t('product.options.color')
        }}
      </h2>
      <span v-if="activeOptionTitle" class="truncate text-base font-normal">
        {{ activeOptionTitle }}
      </span>
      <ButtonIcon
        class="absolute right-2.5 top-2.5"
        icon="x"
        :label="t('global.close')"
        theme="light"
        @click="$emit('on-close')"
        ><IconX class="text-2xl"
      /></ButtonIcon>
    </div>

    <div ref="optionsList" class="">
      <div v-if="store.optionsInStock.length > 0">
        <div class="ml-px grid" :class="gridClass">
          <GalleryOptionItem
            v-for="(option, index) in store.optionsInStock"
            :key="'option-' + index"
            :option="option"
            :is-inside-modal="true"
            :is-active="!!activeOptionId && activeOptionId == option.id"
            @on-click="($event) => $emit('onClick', $event)"
          />
        </div>
      </div>
    </div>

    <h2
      v-if="store.optionsOutOfStock.length > 0"
      class="mb-4 mt-10 px-5 text-lg font-bold text-black"
    >
      {{ t('product.stock.text_oos') }}
    </h2>

    <div
      v-if="store.optionsOutOfStock.length > 0"
      class="ml-px grid"
      :class="gridClass"
    >
      <GalleryOptionItem
        v-for="(option, index) in store.optionsOutOfStock"
        :key="'option-' + index"
        :option="option"
        :is-inside-modal="true"
        :is-active="!!activeOptionId && activeOptionId == option.id"
        @on-click="($event) => $emit('onClick', $event)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import ButtonIcon from '@/shared/components/UI/Buttons/ButtonIcon.vue';
import IconX from '@/shared/components/UI/Icons/IconX.vue';
import { useTranslations } from '@/shared/composables/useTranslations.ts';

import { useProductOptionsStore } from '../../shared/pinia/modules/product-options.ts';
import GalleryOptionItem from './GalleryOptionItem.vue';

const { t } = useTranslations();
const store = useProductOptionsStore();

defineProps<{
  activeOptionId?: number | null;
  activeOptionTitle?: string | '';
}>();

defineEmits(['onClick', 'on-close']);

const optionsList = ref<HTMLElement | null>(null);
const gridClass = computed(() => {
  return store.optionsInStock.length > 7 || store.optionsOutOfStock.length > 7
    ? 'max-xs:grid-cols-3 grid-cols-4 md:grid-cols-6'
    : 'max-xs:grid-cols-3 grid-cols-3 md:grid-cols-4 lg:grid-cols-5';
});

let timeout: number | undefined;

const onScroll = () => {
  store.modalOptionsPosition = optionsList.value?.parentElement?.scrollTop ?? 0;
};

onMounted(() => {
  optionsList.value?.parentElement?.addEventListener('scroll', onScroll, {
    passive: true
  });

  const optionsPosition = store.modalOptionsPosition;
  // NOTE: We need to scroll after modal animation
  timeout = window.setTimeout(() => {
    optionsList.value?.parentElement?.scrollTo({
      left: 0,
      top: optionsPosition
    }); // , behavior: 'smooth'
  }, 200);
});

onBeforeUnmount(() => {
  optionsList.value?.parentElement?.removeEventListener('scroll', onScroll);
  if (timeout) {
    window.clearTimeout(timeout);
  }
});
</script>
